<template>
    <div class="groups-list">
        <Loader :loading="showLoader" />
        <v-card rounded="xl" elevation="4" class="mx-4 mt-4 mb-4 px-4 py-3"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);">
          <div class="d-flex align-center">
            <v-btn icon variant="text" color="white" size="small" @click="goBack">
              <v-icon size="20">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="ml-3 text-h6 font-weight-semibold" style="color: #ffffff;">
              {{ $t('groups.groups') }}
            </div>
            <v-spacer />
            <v-btn class="mr-1" icon variant="text" color="white" size="small" @click="refresh">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn size="small" class="pulse-animation" variant="tonal" @click="CreateEditGroup" color="white">{{
                $t('groups.create') }}</v-btn>
            <img :src="logo" class="toolbar-icon-white ml-2" />
          </div>
        </v-card>

        <v-container class="mt-2 mb-0" style="background-color: transparent; box-shadow: none">
            <v-card-text>
                <v-row align="center" class="mb-2">
                    <v-col cols="auto">
                        <!-- <v-btn icon @click="menu = !menu" color="primary" style="margin-top: -1rem">
                            <v-icon>{{ menu ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
                        </v-btn> -->
                    </v-col>
                    <v-col>
                        <v-text-field v-model="searchQuery" :label="$t('groups.search-groups')" variant="outlined"
                            prepend-inner-icon="mdi-magnify" dense class="mx-4" style="margin-bottom: 0"></v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-container>
        <AdminGroups :groups="filteredGroups" :onEdit="openEditGroup" :onDelete="deleteGroup" />
    </div>
</template>

<script>
import { ref, onMounted, defineComponent, computed } from 'vue'
import { debounce, lodash, map } from 'lodash'
import Loader from '@/components/general/Loader.vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { useRouter } from 'vue-router'
import AdminGroups from '@/components/general/ChildComponents/AdminGroups.vue'
import { getGroups, updateGroup, createGroup, deleteGroupById, clearAllStoredGroups } from '@/controllers/BackboneAPI.js'
import { getUserProfiles, getCampusById, getCampuses, storeData } from '@/controllers/BaseController'
import { dbPromise } from '@/services/utils'
import { removeStoredItem } from '@/services/utils'
import Swal from 'sweetalert2'

export default defineComponent({
    name: 'GroupsList',
    components: {
        Loader,
        AdminGroups,
    },
    setup() {
        const showLoader = ref(true)
        const router = useRouter()
        const logo = ref('')

        const goBack = () => {
            window.history.back()
        }

        const refresh = async () => {
            // Display Swal loader while refreshing groups
            Swal.fire({
                title: 'Refreshing...',
                text: 'Please wait while we refresh the groups list.',
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })

            try {
                // Call the function to clear all stored groups
                await clearAllStoredGroups()

                // Reload the current page to refresh the groups list
                router.go(0)
            } catch (error) {
                console.error('Error refreshing groups:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Refresh Failed',
                    text: 'There was an issue refreshing the groups. Please try again.',
                })
            }
        }

        const navigateTo = (routeName, params = {}) => {
            router.push({ name: routeName, params })
        }

        const CreateEditGroup = () => {
            navigateTo('new-group')
        }

        const groups = ref([])
        const searchQuery = ref('')
        const filteredGroups = computed(() => {
            return groups.value.filter(group => {
                return group?.name.toLowerCase().includes(searchQuery.value.toLowerCase())
            })
        })

        const openEditGroup = async group => {
            try {
                showLoader.value = true
                group = JSON.parse(JSON.stringify(group))
                let GroupId = group.id
                await storeData(GroupId, group)
                navigateTo('edit-group', { id: GroupId })
            } catch (error) {
                console.error('Failed to load data:', error)
                showLoader.value = false
            }
        }

        const deleteGroup = async group => {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: `Do you want to delete the group ${group.name}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d2f8d2',
                cancelButtonColor: '#fbe5e3',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            })

            if (result.isConfirmed) {
                try {
                    showLoader.value = true
                    await deleteGroupById(group.id)
                    groups.value = groups.value.filter(g => g.id !== group.id)
                    showLoader.value = false
                    await refresh()
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: `Group ${group.name} has been deleted.`,
                        timer: 2000,
                        showConfirmButton: false,
                    })
                } catch (error) {
                    console.error('Error deleting group:', error)
                    showLoader.value = false
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'There was a problem deleting the group. Please try again later.',
                    })
                }
            } else {
                Swal.close() // Close the Swal dialog if canceled
            }
        }

        // Debounce the resize listener to avoid performance issues
        const resizeListener = debounce(() => window.innerWidth, 200)

        onMounted(async () => {
            try {
                showLoader.value = true
                logo.value = await getToolbarLogoImage()
                window.addEventListener('resize', resizeListener)
                resizeListener()
                let allGroups = await getGroups()
                allGroups = allGroups.map(group => {
                    return {
                        ...group,
                        name: group?.displayName || group.name,
                        description: group?.description || '',
                    }
                })
                groups.value = allGroups
                console.log('Groups:', allGroups)
                showLoader.value = false
            } catch (error) {
                console.error('Failed to load data:', error)
                showLoader.value = false
            }
        })

        return {
            showLoader,
            logo,
            router,
            goBack,
            navigateTo,
            CreateEditGroup,
            refresh,
            groups,
            searchQuery,
            filteredGroups,
            openEditGroup,
            resizeListener,
            deleteGroup,
        }
    },
})
</script>

<style scoped>
.groups-list {
    padding: 1rem;
}

.toolbar-icon-white {
    height: 30px;
    filter: brightness(0) invert(1);
}
</style>
<!--
GroupsList Component

This component provides a user interface for managing groups. It includes a toolbar with navigation and action buttons, a search field, and a loader for visual feedback during data loading.

Toolbar:
- A back button with an arrow icon allows users to navigate to the previous page.
- A title displaying "Groups" to indicate the current section.
- A refresh button to reload the group list.
- A "Create new" button to navigate to the group creation page.

Search Field:
- A search input field with a magnifying glass icon allows users to filter the list of groups by entering search terms.

Loader:
- A loader component is displayed while data is being loaded, providing visual feedback to the user.

Dynamic Behaviors:
- The back button triggers the goBack method, navigating the user to the previous page.
- The refresh button triggers the refresh method to reload the group list.
- The "Create new" button triggers the CreateEditGroup method, navigating the user to the group creation page.
- The search input field updates the searchQuery data property, which can be used to filter the displayed groups.

Visual Feedback:
- The loader component provides visual feedback during data loading.
- Error handling is implemented to log errors to the console if data loading fails.

Users can leverage this component to easily navigate, search, and manage groups within the application, enhancing their overall experience.
-->
